import { InfiniteTable } from '@/components/_common';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import StatusTag from '@/components/_common/StatusTag';
import { useDeleteInvoice } from '@/core/domain/invoice/invoice/invoice.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Divider, Modal, Space } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useIntl } from 'react-intl';

import { InvoiceSituationColor } from '@/core/domain/invoice/invoice/Invoice.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useInvoiceList } from '@/core/domain/invoice/invoices/invoices.hooks';
import {
  InvoiceData,
  InvoiceListParams,
} from '@/core/domain/invoice/invoices/invoices.types';
import { useDebounce } from '@/hooks';
import { InvoiceSituation } from '@/core/domain/invoice/invoices/invoices.enums';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import formatterPrice from '@/utils/number/formatterPrice';
import { applyCpfCnpjMask } from '@/utils/mask';

interface Props {
  queryParams: InvoiceListParams;
}

type InvoiceSituationNames =
  | 'REPLACED'
  | 'IN_TYPING'
  | 'VALIDATED'
  | 'PROCESSING'
  | 'AUTHORIZED'
  | 'CANCELED';

const InvoiceList = ({ queryParams }: Props) => {
  const intl = useIntl().formatMessage;
  const deleteInvoice = useDeleteInvoice();

  const filter = useDebounce(queryParams);

  const invoiceSettingsByCompany = useInvoiceSettingsByCompany(
    filter?.companyId,
  );

  const showValueTotalInvoice =
    invoiceSettingsByCompany.data?.showValueTotalInvoiceInListing &&
    hasPermissions([Authorities.FISCAL_INVOICE_LIST_VIEW_VALUE]);

  const invoiceList = useInvoiceList({
    ...filter,
    showValueTotalInvoice,
  });

  const invoices = flatInfiniteData(invoiceList.data);

  const handleRemoveClick = async (id: number) => {
    Modal.confirm({
      title: intl({ id: 'invoice.remove.confirm.title' }),
      content: intl({ id: 'invoice.remove.confirm.description' }),
      onOk: async () => await deleteInvoice.mutateAsync(id),
    });
  };

  const columns: ColumnsType<InvoiceData> = [
    {
      dataIndex: 'number',
      title: intl({ id: 'invoice.column.table.invoiceNumber' }),
      render: (value) => value || '-',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      dataIndex: 'serie',
      title: intl({ id: 'invoice.column.table.series' }),
      render: (value) => value || '-',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      dataIndex: 'companyName',
      title: 'Empresa',
      width: 220,
      ellipsis: true,
    },
    {
      dataIndex: 'emissionDate',
      title: intl({ id: 'invoice.column.table.issuanceDate' }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: COLUMNS_WIDTH.date,
      ellipsis: true,
    },
    {
      dataIndex: 'customerName',
      title: intl({ id: 'invoice.column.table.recipient' }),
      render: (_, row) => {
        if (row.customerName) return row.customerName;
        if (row.supplierName) return row.supplierName;
        if (row.insurerName) return row.insurerName;
        return '-';
      },
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    ...(showValueTotalInvoice
      ? [
          {
            dataIndex: 'valueTotalInvoice',
            title: 'Valor Total',
            ellipsis: true,
            render: (value: number) => formatterPrice(value),
            width: COLUMNS_WIDTH.default,
          },
        ]
      : []),
    {
      dataIndex: 'destinataryCpfCnpj',
      title: 'CPF | CNPJ',
      width: COLUMNS_WIDTH.default,
      render: (value: string) => applyCpfCnpjMask(value),
    },
    {
      dataIndex: 'situation',
      title: intl({ id: 'invoice.column.table.situation' }),

      render: (value: InvoiceSituationNames) => {
        if (value) {
          return (
            <StatusTag
              color={InvoiceSituationColor[value]}
              label={intl({ id: `invoice.situation.${value}` })}
            />
          );
        }
        return '-';
      },

      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      title: intl({
        id: 'commons.list.header.action',
      }),
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (_, record) => (
        <Space>
          <EditIcon
            redirect={`/app/fiscal/invoice/${queryParams.invoiceType}/${record.id}/datas`}
            disabled={!hasPermissions([Authorities.FISCAL_INVOICE_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            disabled={
              (record.situation !== InvoiceSituation.IN_TYPING &&
                record.situation !== InvoiceSituation.VALIDATED) ||
              !hasPermissions([Authorities.FISCAL_INVOICE_DELETE])
            }
            onClick={() => handleRemoveClick(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <InfiniteTable
      columns={columns}
      dataSource={invoices}
      loading={invoiceList.isLoading || invoiceSettingsByCompany.isLoading}
      rowKey={(row) => row.id}
      hasNextPage={invoiceList.hasNextPage}
      fetchNextPage={invoiceList.fetchNextPage}
    />
  );
};

export default InvoiceList;
